import { splitDisplayName, resolveMediaUrl, safeAreaTop } from '../../utils/helpers';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme, AcademyThemeScope } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Home, Calendar, Users, MapPin, Settings,
  Share2, MessageSquare, Image, Ticket,
  Bell, MessageCircle, ExternalLink, User, Trophy, X
} from 'lucide-react';
import MessagesPage from '../../messaging/pages/MessagesPage';
import { usePoll } from '../../contexts/PollContext';
import { useProfileImage } from '../../profile/hooks/useProfileImage';
import GuestMenuDropdown from '../../components/common/GuestMenuDropdown';
import AcademySettingsTab from './AcademySettingsTab';
import Tabs from '../../components/common/Tabs';
import { paymentsAPI } from '../../payments/services/PaymentApi';
import { managedPlayersApi } from '../../profile/services/managedPlayersApi';
import ShareModal from '../../components/common/ShareModal';
import OverviewTab from '../tabs/OverviewTab';
import ProgramsTab from '../tabs/ProgramsTab';
import toast from 'react-hot-toast';
import { academyAPI } from '../../services/api';
import SessionFinder from '../components/SessionFinder';
import { useStripeReturn } from '../../payments/services/PaymentStripeHooks';
import AcademyTournamentsTab from '../tabs/AcademyTournamentsTab';
import StaffDirectoryTab from '../tabs/StaffDirectoryTab';
import CourtsPublicTab from '../tabs/CourtsPublicTab';
import GalleryTab from '../tabs/GalleryTab';
import AcademyRatingBadge from '../components/AcademyRatingBadge';

// ── Inline sections (previously in AcademyPublicPage) ─────────────────────────

function FeedsSection({ theme }) {
  return (
    <div className="max-w-2xl mx-auto py-8 text-center">
      <MessageSquare size={48} className={`mx-auto mb-4 ${theme.textMuted} opacity-30`} />
      <h3 className={`text-xl font-bold ${theme.text} mb-2`}>Academy Feed</h3>
      <p className={`${theme.textMuted} text-sm`}>
        News, announcements and updates from the academy will appear here.
      </p>
    </div>
  );
}

// ── Mock academy data ──────────────────────────────────────────────────────────
const mockAcademies = {
  '1': {
    id: '1', slug: 'elite',
    name: 'Elite Tennis Academy',
    description: 'Premier tennis training for players of all skill levels.',
    sportTypeId: 5, sportName: 'Tennis',
    sports: ['Tennis'],
    memberCount: 45, coachCount: 6, classCount: 8,
    backgroundImage: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=1200',
    userRole: 'admin',
    address: '123 Sports Blvd, Los Angeles, CA 90001',
    phone: '(555) 123-4567', email: 'info@elitetennisacademy.com',
    website: 'https://elitetennisacademy.com',
  },
  '2': {
    id: '2', slug: 'national-convent',
    name: 'National Convent School-Sports Academy',
    description: 'Comprehensive multi-sport academy for students from grades 6–12.',
    sportTypeId: 1, sportName: 'Basketball',
    sports: ['Basketball', 'Volleyball', 'Athletics'],
    memberCount: 150, coachCount: 12, classCount: 15,
    backgroundImage: 'https://images.unsplash.com/photo-1519766304817-4f37bba85e1c?w=1200',
    userRole: 'coach',
    address: '456 Education Lane, Boston, MA 02101',
    phone: '(555) 234-5678', email: 'sports@nationalconvent.edu',
  },
  '3': {
    id: '3', slug: 'pickleball-pro',
    name: 'Pickleball Pro Academy',
    description: 'Learn pickleball from certified instructors. Beginner to advanced.',
    sportTypeId: 8, sportName: 'Pickleball',
    sports: ['Pickleball'],
    memberCount: 32, coachCount: 4, classCount: 6,
    backgroundImage: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=1200',
    userRole: null,
    address: '789 Pickleball Ave, Miami, FL 33101',
    phone: '(555) 345-6789', email: 'info@pickleballpro.com',
  },
  '4': {
    id: '4', slug: 'basketball-skills',
    name: 'Basketball Skills Academy',
    description: 'Develop your basketball skills with professional training programs.',
    sportTypeId: 1, sportName: 'Basketball',
    sports: ['Basketball'],
    memberCount: 60, coachCount: 8, classCount: 10,
    backgroundImage: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1200',
    userRole: 'member',
    address: '321 Hoops Street, Chicago, IL 60601',
    phone: '(555) 456-7890', email: 'info@basketballskills.com',
  },
};

// ── Page ───────────────────────────────────────────────────────────────────────
const AcademyDetailsPageInner = () => {
  const { academyId, slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const { user } = useAuth();
  const activeTab = searchParams.get('tab') || 'overview';
  const setActiveTab = (tab) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('tab', tab);
    // Clear program drill-down when switching top-level tabs
    next.delete('program');
    next.delete('view');
    return next;
  });
  const [showShareModal, setShowShareModal] = useState(false);
  // Staff member to open a 1:1 with when the Messages tab mounts. Held here
  // rather than put in the URL so the academy's query string stays clean —
  // MessagesPage creates the channel if one does not exist yet.
  const [dmTarget, setDmTarget] = useState(null);

  // Switch to Overview (if not already there) then scroll to the Ratings &
  // Reviews section once it's mounted. activeTab flips via a URL search
  // param, which re-renders OverviewTab a tick later than this call — the
  // short delay lets that render land before we try to scroll to it.
  const goToReviews = () => {
    setActiveTab('overview');
    setTimeout(() => {
      document.getElementById('ratings-reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  /**
   * Open the write-review dialog from the header.
   *
   * THE DIALOG BELONGS TO OverviewTab, which owns the reviews card and the
   * refresh that follows a submission. Rather than lift it up here — and split
   * the state that keeps the card in step with it — the header asks for it by
   * event and the tab answers. Overview is selected first, because the tab has
   * to be mounted to hear it.
   */
  const goToWriteReview = () => {
    setActiveTab('overview');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('kynaix:write-review'));
    }, 300);
  };

  const [academy, setAcademy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const { notifications } = usePoll();
  const unreadCount = notifications?.unread ?? 0;
  const { imageUrl: profileImageUrl } = useProfileImage(user?.id, user?.profileImage);

  const loadAcademy = useCallback(() => {
    const key = slug || academyId;
    if (!key) { setNotFound(true); setLoading(false); return; }
    import('../../services/api').then(({ academyAPI }) =>
      academyAPI.getById(key)
        .then(data => setAcademy(data))
        .catch(() => setNotFound(true))
        .finally(() => setLoading(false))
    );
  }, [slug, academyId]);

  useEffect(() => { loadAcademy(); }, [loadAcademy]);



  // The server decides this (isAcademyAdmin) and sends it. The role-string test
  // is only a fallback for a payload from an older API.
  const isAdmin = academy?.isAdmin ?? (academy?.userRole === 'owner' || academy?.userRole === 'admin');
  // Owner/admin implicitly have every capability; a non-admin staff member
  // (e.g. a coach) only has what's been explicitly granted via the Staff
  // Settings permissions matrix — see academiesController.js's getAcademy.
  const myCapabilities = academy?.myCapabilities || [];
  const hasAnyCapability = isAdmin || myCapabilities.length > 0;

  // What this academy runs. Absent means on: an academy that has never touched
  // these keeps everything, and a payload that fails to load must not silently
  // strip the navigation.
  const features = academy?.features || {};
  const on = (key) => features[key] !== false;

  const tabs = [
    { id: 'overview',  label: 'Overview',       shortLabel: 'Overview', icon: Home },
    ...(on('enableFeeds')
      ? [{ id: 'feeds', label: 'Feeds', shortLabel: 'Feeds', icon: MessageSquare }]
      : []),
    // The four ways to take part, under one heading.
    //
    // They were four top-level tabs beside Overview, Feeds, Staff, Gallery and
    // Settings — nine, which is more than a phone can show and more than anyone
    // reads. They also answer the same question: what can I join, and when?
    // Grouping them says that, and leaves the top row short enough to fit.
    //
    // The children keep their own ids, so ?tab=programs, ?tab=play,
    // ?tab=tournaments and ?tab=courts all still address exactly what they did
    // — including the returnPath handed to Stripe, which is a live URL in
    // sessions that may be mid-flight right now.
    {
      id: 'letsplay', label: "Let's Play", shortLabel: 'Play', icon: Ticket,
      children: [
        { id: 'programs',    label: 'Programs',   icon: Calendar },
        // The bookable nights, as opposed to the offerings that define them.
        // Same component as the global feed, scoped to this academy.
        ...(on('enableOpenPlay') ? [{ id: 'play', label: 'Open Play', icon: Ticket }] : []),
        ...(on('enableTournaments') ? [{ id: 'tournaments', label: 'Tournaments', icon: Trophy }] : []),
        ...(on('enableCourtBookings') ? [{ id: 'courts', label: 'Courts', icon: MapPin }] : []),
      ],
    },
    { id: 'staff',     label: 'Staff',           shortLabel: 'Staff',    icon: Users },
    { id: 'gallery',   label: 'Gallery',         shortLabel: 'Gallery',  icon: Image },
    // Shown for anyone with at least one capability, not just full admins — a
    // coach granted only manage_programs still needs to reach the Settings
    // shell to use that one sub-tab. AcademySettingsTab gates each sub-tab
    // individually against myCapabilities.
    ...(hasAnyCapability ? [{ id: 'settings', label: 'Settings', shortLabel: 'Settings', icon: Settings }] : []),
  ];

  // The session a player is booking. Held here rather than inside SessionFinder
  // so both placements of that component share one dialog.
  // The order awaiting payment, if any. Null means no checkout on screen.
  const [checkout, setCheckout] = useState(null);
  // Bumped after a booking so the list refetches: spots, roster and the price
  // a player now owes have all changed.
  const [bookingsVersion, setBookingsVersion] = useState(0);

  // Stripe sends the payer back here, and until this was mounted nobody was
  // waiting: the money was taken and the order sat pending, because "?payment=
  // success" in the URL settles nothing on its own. This asks the server to
  // re-read the session from Stripe, which is what actually marks it paid.
  //
  // Mounted on the page rather than inside the Open Play tab — a tab that has
  // not been visited is not rendered, and the redirect would land on nothing.
  const { result: paymentResult, verifying: verifyingPayment, dismiss: dismissPaymentResult } = useStripeReturn({
    onPaid: () => setBookingsVersion(v => v + 1),
  });

  /**
   * Book opens checkout. The place is taken at confirm, not here.
   *
   * It used to book first so the seat was held while the payer was on Stripe.
   * That made the registrant unchoosable — the booking already existed by the
   * time anyone could say who it was for — and left a place taken whenever
   * somebody opened the dialog to look at the price. Now nothing is held until
   * Review is confirmed, which is also when we know whose place it is.
   */

  /**
   * Booking now ends on the checkout PAGE rather than in a dialog.
   *
   * The place still is not held until a registrant is known — that constraint
   * is unchanged and is why the picker comes first. What changed is where the
   * money is taken: a dialog cannot survive the trip to stripe.com, so the
   * payer used to come back to a list with nothing said. /checkout is an
   * address, which means Stripe can simply send them back to it.
   *
   * A player who manages nobody is never asked who it is for — that dialog had
   * one option, and a step with one answer is a click that teaches nothing.
   */
  /**
   * Book goes straight to checkout.
   *
   * It used to ask who the place was for in a dialog here, hold the place, then
   * send the payer to checkout — where the same question is now step one. Two
   * screens asking the same thing, and a place held for somebody who might
   * never pay.
   *
   * Nothing is booked now. The order carries what to create, and the place is
   * taken when the payment lands — so backing out, closing the tab or pressing
   * back costs nothing and holds nobody's seat.
   */
  /* ASK WHO BEFORE RAISING ANYTHING. Same chooser as checkout and as the
   * programme Register, so the payer meets one question in one shape. */
  const [raisingBooking, setRaisingBooking] = useState(false);

  const handleBookSession = async (session, registrantUserId) => {
    /* ONE PRESS, ONE ORDER.
     *
     * The button used to open a dialog, which is its own answer to being pressed
     * twice. Now it goes straight to checkout, and the second press lands while
     * the first is still in flight. The server hands back the same live order
     * rather than billing twice, so nothing is charged twice — but two
     * navigations racing is a screen that flickers and lands somewhere
     * unexpected. */
    if (raisingBooking) return;
    setRaisingBooking(true);
    try {
      const order = await paymentsAPI.startBooking({
        items: [{
          subjectType: 'session',
          subjectId: String(session.id),
          // Named here rather than guessed at settlement. Null means "my
          // account", which is a real answer; absent is not, and the server
          // refuses it.
          playerId: registrantUserId ?? null,
        }],
        pendingPayload: { type: 'session', eventId: String(session.id) },
        // A night that costs nothing still needs an order: the place is not
        // held until that order settles. Checkout drops the payment step when
        // the total is zero.
        allowFree: true,
      });
      const from = `${window.location.pathname}${window.location.search}`;
      /* SAY WHEN THE ORDER WAS ALREADY THERE.
       *
       * One live order per person per session, so pressing Book on a night
       * already booked hands back the EXISTING order rather than raising one.
       * Checkout withdraws the order when the payer backs out — right for an
       * order this press just made, and destruction for one it merely reopened:
       * going in to look at a place and pressing "Cancel and go back" cancelled
       * the booking and released the seat, with nothing saying so. */
      navigate(`/checkout?order=${order.id}`
        + (order.reused ? '&reused=1' : '&raised=1')
        + `&from=${encodeURIComponent(from)}`
        + `&academy=${encodeURIComponent(slug || academy.id)}`
        + `&title=${encodeURIComponent(session.title || 'Open Play')}`);
    } catch (err) {
      toast.error(err?.message || 'Could not start checkout');
    } finally {
      setRaisingBooking(false);
    }
  };

  const renderTabContent = () => {
    if (!academy) return null;
    switch (activeTab) {
      case 'overview':  return <OverviewTab academy={academy} onTabChange={setActiveTab} />;
      case 'feeds':     return <FeedsSection theme={theme} />;
      case 'programs':  return <ProgramsTab academyId={academy.id} userRole={academy.userRole} academy={academy} />;
      // Same component as the global feed — academyId is the only difference.
      /* STRAIGHT TO CHECKOUT. Booking used to stop at a "who is this for?"
       * dialog first, which asked the same question checkout asks on its own
       * first step — `collectRegistrant` is on for sessions. Two screens, one
       * question, and the first one stood between the button and the thing it
       * said it did.
       *
       * The place is held for the payer and the registrant is named at checkout;
       * changing it there moves the seat with it, so the answer given later is
       * as good as the answer given first. */
      case 'play':      return <SessionFinder academyId={academy.id} onBook={(session) => handleBookSession(session, null)} refreshKey={bookingsVersion} />;
      case 'tournaments': return <AcademyTournamentsTab academy={academy} userRole={academy.userRole} />;
      case 'staff':     return <StaffDirectoryTab academyId={academy.id} onOpenMessages={(member) => {
        // The staff directory returns a single `name`, not first/last — mapping
        // the fields that don't exist is what made the chat header read
        // "Unknown". `relationship` tells the chat this is a real contact the
        // app offered, so it isn't labelled a former teammate and left read-only.
        setDmTarget(member?.userId ? {
          id: member.userId,
          name: member.name,
          ...splitDisplayName(member.name),
          profileImage: member.profileImage,
          relationship: member.role || 'Academy staff',
        } : null);
        setActiveTab('messages');
      }} />;
      case 'messages':  return <MessagesPage embedded initialDmUser={dmTarget} />;
      case 'courts':    return <CourtsPublicTab academyId={academy.id} academy={academy} />;
      case 'gallery':   return <GalleryTab academyId={academy.id} userRole={academy.userRole} />;
      case 'settings':
        // Direct-URL guard (?tab=settings) — nav button hiding alone isn't
        // enough, since activeTab is just a query param anyone can type.
        if (!hasAnyCapability) return <OverviewTab academy={academy} onTabChange={setActiveTab} />;
        return (
          <AcademySettingsTab
            academyId={academy.id}
            userRole={academy.userRole}
            isAdmin={isAdmin}
            myCapabilities={myCapabilities}
            onAcademyUpdate={loadAcademy}
          />
        );
      default:          return <OverviewTab academy={academy} onTabChange={setActiveTab} />;
    }
  };

  if (loading) {
    return (
      <div className={`${theme.background} min-h-screen flex items-center justify-center`}>
        <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${theme.borderAccent}`} />
      </div>
    );
  }

  if (notFound || !academy) {
    return (
      <div className={`${theme.background} min-h-screen flex items-center justify-center`}>
        <div className="text-center">
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Academy not found</h2>
          <button onClick={() => navigate('/academies')} className={`${theme.link} hover:underline`}>
            Back to Academies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme.background} min-h-screen`}>
      {/* Hero */}
      <div
        className={`relative flex flex-col justify-end ${academy.backgroundImage ? 'min-h-[20rem]' : 'min-h-[16rem]'}`}
        style={{
          // Stored relative (/images/...), which a native web view resolves
          // against capacitor://localhost — the hero was simply blank there.
          backgroundImage: academy.backgroundImage
            ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.65)), url(${resolveMediaUrl(academy.backgroundImage)})`
            : 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,64,175,0.9) 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Top bar: platform link + user actions.
            Absolutely positioned at top-0 over the hero, so unlike the app's
            own header it sits directly under the status bar and camera cutout
            and has to inset itself. */}
        <div className="absolute top-0 left-0 right-0 z-10" style={{ paddingTop: safeAreaTop() }}>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            {/* An academy running this page as its own front door may not want
                a way out of it in the corner of every screen. */}
            {on('enableKynaixLink') ? (
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors text-sm font-medium"
              >
                <ExternalLink size={14} />
                Kynaix Sports
              </button>
            ) : <span />}
            <div className="flex items-center gap-1.5">
              {user ? (
                <>
                  {/* Chats off means no way in, not a bubble that opens an
                      empty screen. */}
                  {on('enableChats') && (
                    <button
                      onClick={() => { setDmTarget(null); setActiveTab('messages'); }}
                      className="p-2 bg-black/20 hover:bg-black/40 rounded-full transition-colors backdrop-blur-sm"
                      title="Messages"
                    >
                      <MessageCircle size={17} className="text-white" />
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/notifications')}
                    className="relative p-2 bg-black/20 hover:bg-black/40 rounded-full transition-colors backdrop-blur-sm"
                    title="Notifications"
                  >
                    <Bell size={17} className="text-white" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => navigate('/profile')}
                    className="w-8 h-8 rounded-full overflow-hidden bg-black/20 backdrop-blur-sm hover:ring-2 hover:ring-white/50 transition-all flex items-center justify-center"
                    title="Profile"
                  >
                    {profileImageUrl
                      ? <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                      : <User size={15} className="text-white" />
                    }
                  </button>
                </>
              ) : (
                <GuestMenuDropdown buttonClassName="p-2 bg-black/20 hover:bg-black/40 rounded-full transition-colors backdrop-blur-sm" />
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto w-full px-4 pt-16 pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-end">
            <div className="flex items-end gap-3 sm:gap-4 text-white min-w-0">
              {/* Logo */}
              {academy.logo && (
                <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white/30 shadow-lg flex-shrink-0 mb-1">
                  {/* resolveMediaUrl, not the raw value: logos are stored root-relative
                      ("/images/group_assets/….png"). On the web that resolves against the
                      site origin and works; in the native shell the origin is
                      capacitor://localhost, so it resolves against the app bundle and 404s
                      — the logo simply did not appear on mobile. The hero background two
                      hundred lines up already went through here; the logo was missed. */}
                  <img src={resolveMediaUrl(academy.logo)} alt={academy.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 break-words">{academy.name}</h1>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(academy.sportTypes || []).map(s => (
                    <span key={s.id} className={`px-2.5 py-1 ${theme.tabActive} rounded-lg text-xs sm:text-sm font-semibold`}>{s.sportName}</span>
                  ))}
                  {academy.userRole && (
                    <span className={`px-2.5 py-1 rounded-lg text-xs sm:text-sm font-semibold ${
                      isAdmin ? 'bg-red-500'
                      : academy.userRole === 'coach' ? 'bg-yellow-500'
                      : 'bg-green-500'
                    }`}>
                      {academy.userRole.charAt(0).toUpperCase() + academy.userRole.slice(1)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <AcademyRatingBadge
                academyId={academy.id}
                onClick={goToReviews}
                onWrite={goToWriteReview}
              />
              <button onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors">
                <Share2 size={18} /><span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className={`${theme.card} border-b sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-4">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} showMobileMenu={true} />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {renderTabContent()}
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        entity={{ id: academy.id, slug: academy.slug, name: academy.name, description: academy.description }}
        entityType="academy"
        urlPattern="/academy/{slug}"
        currentUser={user}
      />

      {/* The payment confirmation, which has to be possible to get rid of.
          It had no dismiss and no expiry, so it sat over the page until a full
          reload — on every tab the payer visited afterwards. A receipt should
          outlast a toast, not the whole visit. */}
      {(verifyingPayment || paymentResult) && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] max-w-md w-[calc(100%-2rem)]">
          <div className={`${theme.card} border rounded-xl shadow-lg px-4 py-3 text-sm ${theme.text} flex items-start gap-3`}>
            <span className="min-w-0 flex-1">
              {verifyingPayment ? 'Confirming your payment…' : paymentResult?.message}
            </span>
            {!verifyingPayment && (
              <button
                onClick={dismissPaymentResult}
                aria-label="Dismiss"
                className={`${theme.textMuted} hover:opacity-100 opacity-70 transition flex-shrink-0`}
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      )}


    </div>
  );
};

/**
 * The academy's theme has to be in place ABOVE the screen that reads it.
 *
 * useTheme() resolves from the nearest provider, so a page cannot both
 * establish the academy's theme and render in it — by the time it could set
 * one, it has already read the other. This shell fetches nothing: it takes the
 * key from the same cache the page uses and puts the scope where descendants
 * can see it, which includes Design Studio and its live preview.
 */
const AcademyDetailsPage = () => {
  const { academyId, slug } = useParams();
  const [themeKey, setThemeKey] = useState(null);

  useEffect(() => {
    const key = slug || academyId;
    if (!key) return;
    let live = true;
    academyAPI.getById(key)
      .then(a => { if (live) setThemeKey(a?.themeKey || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [slug, academyId]);

  return (
    <AcademyThemeScope themeKey={themeKey}>
      <AcademyDetailsPageInner />
    </AcademyThemeScope>
  );
};

export default AcademyDetailsPage;
